import { Tooltip as TooltipPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Mount once at the app root. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={150}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

type TooltipProps = {
  /** Plain text or rich content (e.g. rule / reason / evidence lines). */
  content: ReactNode;
  /** A single focusable element (button, link, or an element with tabIndex=0). */
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Suppress without unmounting (e.g. only show in the collapsed rail). */
  disabled?: boolean;
  className?: string;
};

/**
 * Fixed-position tooltip, portaled to <body> so a table with overflow never
 * clips it. Opens on hover AND keyboard focus; the trigger gets
 * aria-describedby. Tooltips supplement — never carry the only copy of —
 * information a touch user needs.
 */
export function Tooltip({ content, children, side = 'top', align = 'center', disabled = false, className }: TooltipProps) {
  if (disabled) return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={cn(
            'z-60 flex max-w-[320px] flex-col gap-1 rounded-[8px] bg-fg px-3 py-2.5 text-xs text-bg shadow-pop data-[state=delayed-open]:animate-fade-in',
            className,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/** A de-emphasised line inside tooltip content (e.g. the evidence line). */
export function TooltipMuted({ children }: { children: ReactNode }) {
  return <span className="text-[color-mix(in_srgb,var(--bg)_70%,transparent)]">{children}</span>;
}
